/**
 * Modelova citlivost dluhopisu na zmenu vynosu (scenare -500 az +500 bp).
 *
 * Postup: z ciste ceny a vlastniho AUV k dnesku se dopocita spinava cena, z ni
 * YTM a modifikovana durace. Zmena ceny pro scenar je presne preceneni zbyvajiciho
 * cash flow pri vynosu y + dy (paralelni posun krivky). Odhad z durace a konvexity
 * se nepouziva: parabola se pri velkem posunu otaci a cena by s rostoucim vynosem
 * zacala rust.
 *
 * AUV se pocita z rozpisu kuponu, ne z kotace: AUV v kotaci je k vyporadani,
 * takze tesne pred vyplatou kuponu by spinava cena byla nadhodnocena.
 */

export const SCENARIOS_BP = [-500, -400, -300, -200, -100, 100, 200, 300, 400, 500]

const DAYS_IN_YEAR = 365.25
const MS_IN_DAY = 86400000
// Kupon pod 0,5 % nominalu je v datech temer vzdy spatne zadany (napr. 7,25 misto 725).
const MIN_COUPON_RATE = 0.005
const YIELD_MIN = -0.5
// Vynos nad 50 % znamena papir v problemech nebo chybna data, durace by nedavala smysl.
const YIELD_MAX = 0.5

export interface BondParams {
  /** Rocni kupon na kus, ve stejnych jednotkach jako nominal (55 pri nominalu 1000). */
  coupon: number | null | undefined
  maturityDate: unknown
  couponFrequency: string | null | undefined
  nominal: number | null | undefined
}

export interface BondSensitivity {
  ytm: number
  modifiedDuration: number
  dirtyPrice: number
  /** Hodnota zbyvajiciho cash flow pri danem vynosu. */
  presentValue: (y: number) => number
}

const paymentsPerYear = (couponFrequency: string | null | undefined): number => {
  if (couponFrequency === 'CF_QUARTERLY') return 4
  if (couponFrequency === 'CF_ANNUAL') return 1
  return 2
}

/** BE posila LocalDate jako retezec nebo pole [rok, mesic, den]; pocita se v UTC kvuli letnimu casu. */
const toUtcDay = (value: unknown): number | null => {
  if (Array.isArray(value) && value.length >= 3) return Date.UTC(value[0], value[1] - 1, value[2])
  if (value instanceof Date && !isNaN(value.getTime())) {
    return Date.UTC(value.getFullYear(), value.getMonth(), value.getDate())
  }
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (match) return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  }
  return null
}

/** Odecte mesice od data; konec mesice se orizne (31.8. minus 6 mesicu = 28.2.). */
const minusMonths = (day: number, months: number): number => {
  const date = new Date(day)
  const year = date.getUTCFullYear()
  const month = date.getUTCMonth() - months
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
  return Date.UTC(year, month, Math.min(date.getUTCDate(), lastDay))
}

/** Vraci parametry citlivosti, nebo null, kdyz na vypocet nejsou data (dluhopis se pak zobrazi s pomlckami). */
export const computeBondSensitivity = (
  params: BondParams,
  cleanPrice: number,
  today: Date = new Date()
): BondSensitivity | null => {
  const { coupon, nominal } = params
  const maturity = toUtcDay(params.maturityDate)
  const todayDay = toUtcDay(today)
  if (!coupon || coupon <= 0 || !nominal || nominal <= 0 || !cleanPrice || cleanPrice <= 0) return null
  if (maturity === null || todayDay === null || maturity <= todayDay) return null
  if (coupon / nominal < MIN_COUPON_RATE) return null

  const perYear = paymentsPerYear(params.couponFrequency)
  const monthsStep = 12 / perYear
  const couponAmount = coupon / perYear

  // Kotvou rozpisu je maturita, stejne jako v CouponScheduleUtils na BE.
  const flows: { years: number; amount: number }[] = []
  let periods = 0
  let previousPayment = maturity
  while (previousPayment > todayDay) {
    flows.unshift({
      years: (previousPayment - todayDay) / MS_IN_DAY / DAYS_IN_YEAR,
      amount: couponAmount + (periods === 0 ? nominal : 0),
    })
    periods += 1
    previousPayment = minusMonths(maturity, monthsStep * periods)
  }
  const nextPayment = minusMonths(maturity, monthsStep * (periods - 1))
  const accrued = (couponAmount * (todayDay - previousPayment)) / (nextPayment - previousPayment)
  const dirtyPrice = cleanPrice + accrued

  const presentValue = (y: number) =>
    flows.reduce((sum, flow) => sum + flow.amount / Math.pow(1 + y / perYear, flow.years * perYear), 0)

  if (presentValue(YIELD_MIN) < dirtyPrice || presentValue(YIELD_MAX) > dirtyPrice) return null
  let low = YIELD_MIN
  let high = YIELD_MAX
  for (let i = 0; i < 100; i++) {
    const middle = (low + high) / 2
    if (presentValue(middle) > dirtyPrice) low = middle
    else high = middle
  }
  const ytm = (low + high) / 2

  const base = 1 + ytm / perYear
  const macaulay = flows.reduce(
    (sum, { years, amount }) => sum + (years * amount) / Math.pow(base, years * perYear),
    0
  )

  return {
    ytm,
    modifiedDuration: macaulay / dirtyPrice / base,
    dirtyPrice,
    presentValue,
  }
}

/** Relativni zmena ceny pro scenar v bazickych bodech (0,1 = 10 %). */
export const priceChange = (sensitivity: BondSensitivity, bp: number): number =>
  sensitivity.presentValue(sensitivity.ytm + bp / 10000) / sensitivity.dirtyPrice - 1
